import { useMemo } from "react";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { setDynamicsDeletableField } from "app/pages/sales-intelligence/sub-modules/management/store/actions/export/dynamics";
import { setSfDeletableField } from "app/pages/sales-intelligence/sub-modules/management/store/actions/export/salesforce";
import { setHsDeletableField } from "app/pages/sales-intelligence/sub-modules/management/store/actions/export/hubspot";
import {
    addSettingsDynamicsExportThunk,
    deleteDynamicsSettingLocalThunk,
    updateDynamicsExportSettingsThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/export";
import { getDynamicsExcludeStatusFieldsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/exclude";
import {
    addSettingsExportThunk,
    deleteHsSettingLocalThunk,
    updateHsExportSettingsThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/export";
import {
    addSettingsSalesForceExportThunk,
    deleteSfSettingLocalThunk,
    updateSalesForceExportSettingsThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/export";
import {
    selectHsAccountsColumns,
    selectHsContactsColumns,
    selectHsContactsNotSelectedSettings,
    selectHsCurrentSettings,
    selectHsIsColumnsLoading,
    selectHsIsLoadingSettings,
    selectHsNotSelectedSettings,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspot/exportSelectors";
import {
    selectIsLoadingSettings,
    selectSfAccountsColumns,
    selectSfCompanyAsLeadsColumns,
    selectSfCompanyAsLeadsNotSelectedSettings,
    selectSfContactsColumns,
    selectSfContactsNotSelectedSettings,
    selectSfCurrentSettings,
    selectSfIsColumnsLoading,
    selectSfLeadsColumns,
    selectSfLeadsNotSelectedSettings,
    selectSfNotSelectedSettings,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforce/exportSelectors";
import {
    selectDynamicsAccountsColumns,
    selectDynamicsCompanyAsLeadsColumns,
    selectDynamicsCompanyAsLeadsNotSelectedSettings,
    selectDynamicsContactsColumns,
    selectDynamicsContactsNotSelectedSettings,
    selectDynamicsCurrentSettings,
    selectDynamicsIsColumnsLoading,
    selectDynamicsIsLoadingSettings,
    selectDynamicsLeadsColumns,
    selectDynamicsLeadsNotSelectedSettings,
    selectDynamicsNotSelectedSettings,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamics/exportSelectors";
import {
    selectDynamicsExcludeStatusFields,
    selectDynamicsIsLoadingExcludeStatusFields,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import {
    selectHsExcludeStatusFields,
    selectHsIsLoadingExcludeStatusFields,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import {
    selectSfExcludeStatusFields,
    selectSfIsLoadingExcludeStatusFields,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import { getSfExcludeStatusFieldsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/exclude";
import { getHsExcludeStatusFieldsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/exclude";

export const useManageExportTableProps = (integrationName: Integration) => {
    const allProps = useMemo(
        () =>
            ({
                salesforce: {
                    onDelete: setSfDeletableField,
                    data: selectSfCurrentSettings,
                    filteredData: {
                        contacts: selectSfContactsNotSelectedSettings,
                        leads: selectSfLeadsNotSelectedSettings,
                        accounts: selectSfNotSelectedSettings,
                        companyAsLeads: selectSfCompanyAsLeadsNotSelectedSettings,
                    },
                    isLoadingState: selectIsLoadingSettings,
                    addSettingsThunk: addSettingsSalesForceExportThunk,
                    editSettingThunk: updateSalesForceExportSettingsThunk,
                    isLoadingCrmFields: selectSfIsLoadingExcludeStatusFields,
                    deleteLocal: deleteSfSettingLocalThunk,
                    columnsSelectors: {
                        accounts: selectSfAccountsColumns,
                        contacts: selectSfContactsColumns,
                        leads: selectSfLeadsColumns,
                        companyAsLeads: selectSfCompanyAsLeadsColumns,
                    },
                    isAllColumnsLoading: selectSfIsColumnsLoading,
                    crmFields: selectSfExcludeStatusFields,
                    fetchCrmFields: getSfExcludeStatusFieldsThunk,
                },
                hubspot: {
                    onDelete: setHsDeletableField,
                    data: selectHsCurrentSettings,
                    filteredData: {
                        contacts: selectHsContactsNotSelectedSettings,
                        accounts: selectHsNotSelectedSettings,
                    },
                    isLoadingState: selectHsIsLoadingSettings,
                    addSettingsThunk: addSettingsExportThunk,
                    editSettingThunk: updateHsExportSettingsThunk,
                    isLoadingCrmFields: selectHsIsLoadingExcludeStatusFields,
                    deleteLocal: deleteHsSettingLocalThunk,
                    columnsSelectors: {
                        accounts: selectHsAccountsColumns,
                        contacts: selectHsContactsColumns,
                    },
                    isAllColumnsLoading: selectHsIsColumnsLoading,
                    crmFields: selectHsExcludeStatusFields,
                    fetchCrmFields: getHsExcludeStatusFieldsThunk,
                },
                dynamics: {
                    onDelete: setDynamicsDeletableField,
                    data: selectDynamicsCurrentSettings,
                    filteredData: {
                        contacts: selectDynamicsContactsNotSelectedSettings,
                        leads: selectDynamicsLeadsNotSelectedSettings,
                        accounts: selectDynamicsNotSelectedSettings,
                        companyAsLeads: selectDynamicsCompanyAsLeadsNotSelectedSettings,
                    },
                    isLoadingState: selectDynamicsIsLoadingSettings,
                    addSettingsThunk: addSettingsDynamicsExportThunk,
                    editSettingThunk: updateDynamicsExportSettingsThunk,
                    isLoadingCrmFields: selectDynamicsIsLoadingExcludeStatusFields,
                    deleteLocal: deleteDynamicsSettingLocalThunk,
                    columnsSelectors: {
                        accounts: selectDynamicsAccountsColumns,
                        contacts: selectDynamicsContactsColumns,
                        leads: selectDynamicsLeadsColumns,
                        companyAsLeads: selectDynamicsCompanyAsLeadsColumns,
                    },
                    isAllColumnsLoading: selectDynamicsIsColumnsLoading,
                    crmFields: selectDynamicsExcludeStatusFields,
                    fetchCrmFields: getDynamicsExcludeStatusFieldsThunk,
                },
            }[integrationName]),
        [integrationName],
    );

    return allProps;
};
